<template>
  <div class="condition-row">
    <span class="condition-row__word">Si</span>
    <Dropdown
      class="condition-row__field"
      :options="customFormOptions"
      :selected="conditionField"
      :selection.sync="conditionField"
      :search="true"
      :clearable="true"
      name="conditioning-field"
    />
    <span class="condition-row__word">=</span>
    <ExtraForm
      v-if="conditioningCustForm"
      :id="conditioningCustForm.label"
      :key="`condition-${conditioningCustForm.field_type}`"
      class="condition-row__field"
      name="conditioning-value"
      :field="conditionValueField"
      :use-value-only="true"
      :hide-label="true"
      @update:value="updateConditionValue($event)"
    />
    <span
      v-else
      class="condition-row__pending"
    >valeur</span>
    <span class="condition-row__word">alors</span>
    <ExtraForm
      v-if="isForcedValue"
      :id="`forced-value-for-${customForm.name}`"
      :key="`forced-${forcedField.field_type}`"
      class="condition-row__field"
      name="forced-value"
      :field="forcedField"
      :use-value-only="true"
      :hide-label="true"
      @update:value="updateForcedValue($event)"
    />
    <span
      v-else
      class="condition-row__word"
    >le champ est affiché</span>
  </div>
</template>

<script>
import Dropdown from '@/components/Dropdown.vue';
import ExtraForm from '@/components/ExtraForm';

export default {
  name: 'CustomFormConditionalField',

  components: {
    Dropdown,
    ExtraForm,
  },

  props: {
    config: {
      type: Object,
      default: () => {}
    },
    form: {
      type: Object,
      default: () => {}
    },
    storedCustomForm: {
      type: Object,
      default: () => {}
    },
    customForms: {
      type: Array,
      default: () => []
    },
    isForcedValue: {
      type: Boolean,
      default: false
    },
  },

  data() {
    return {
      customFormOptions: [],
      unsubscribe: null,
    };
  },

  computed: {
    customForm () {
      // if the customForm has been updated in the store return it
      if (this.storedCustomForm && this.storedCustomForm.name) return this.storedCustomForm;
      // else if the custom for is not yet in store, build the same structure as a stored one to pass it to ExtraForm component
      let customFormInCreation = {};
      for (const el in this.form) {
        customFormInCreation[el] = this.form[el].value;
      }
      return customFormInCreation;
    },
    conditioningCustForm() {
      return this.customForms.find((custForm) => custForm.name === this.config.conditionField) || null;
    },

    conditionValueField() {
      return this.widgetField(this.conditioningCustForm, this.config.conditionValue);
    },

    forcedField() {
      const source = { ...(this.customForm || {}) };
      const localType = this.form && this.form.field_type ? this.form.field_type.value : null;
      if (localType) {
        source.field_type = localType;
      }
      if (this.form && this.form.options && this.form.options.value !== undefined) {
        source.options = this.form.options.value;
      }
      return this.widgetField(source, this.config ? this.config.forcedValue : null);
    },

    isListField() {
      return ['list', 'pre_recorded_list', 'multi_choices_list'].includes(this.conditioningCustForm.field_type);
    },
      
    conditionField: {
      get() {
        return this.conditioningCustForm ? this.formatOptionLabel(this.conditioningCustForm) : '';
      },
      set(newValue) {
        //* set the value selected in the dropdown
        const newConfig = newValue ? { conditionField: newValue.value } : {};
        if (this.config.dataKey) { // forced value being a list, a consistent identifier is needed by Vue to track component thus we use a dataKey
          newConfig['dataKey'] = this.config.dataKey; // and this dataKey need to be kept across modification
        }
        if (this.config.forcedValue !== undefined) {
          // forced value depend on customForm type and won't change here, since it is set when adding the condition, we neet to keep it
          newConfig['forcedValue'] = this.config.forcedValue;
        }
        this.$emit('update:config', newConfig);
      },
    },
  },

  mounted() { // listening to store mutation, since changes of property nested in customForms are not detected
    this.updateCustomFormOptions();
    this.unsubscribe = this.$store.subscribe(({ type }) => {
      if (type === 'feature-type/UPDATE_CUSTOM_FORM') {
        this.updateCustomFormOptions();
      }
    });
  },

  beforeDestroy() {
    this.unsubscribe();
  },

  methods: {
    formatOptionLabel: (custForm) => `${custForm.label} (${custForm.name})`,

    updateCustomFormOptions() {
      this.customFormOptions = this.customForms
        .filter((custForm) => (
          custForm.label && custForm.name && // check that customForm has defined properties
          custForm.name !== this.customForm.name && // filter out this customForm itself
          custForm.field_type !== 'char' && custForm.field_type !== 'text'
        ))
        .map((custForm) => {
          return {
            name: this.formatOptionLabel(custForm),
            value: custForm.name,
          };
        });
    },

    updateConditionValue(conditionValue) {
      this.$emit('update:config', { ...this.config, conditionValue });
    },

    updateForcedValue(forcedValue) {
      this.$emit('update:config', { ...this.config, forcedValue });
    },

    widgetField(source, value) {
      const field = { ...(source || {}) };
      const type = field.field_type;
      field.field_type = type && typeof type === 'object' ? type.value : type;
      field.value = value;
      if (field.field_type === 'boolean') {
        field.value = value === true || value === 'true';
      }
      return field;
    },
  }
};

</script>

<style scoped>
.condition-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.45rem 0.6rem;
}

.condition-row__word {
  color: #3a4453;
}

.condition-row__pending {
  color: #9aa1ab;
}

.condition-row__field {
  flex: 1 1 9rem;
  min-width: 8rem;
  margin: 0 !important;
}

.condition-row >>> .field {
  margin: 0;
}

.condition-row >>> .ui.checkbox {
  min-height: 1.2rem;
}

.condition-row >>> .ui.checkbox label:before,
.condition-row >>> .ui.checkbox .box:before {
  background: #fff;
  border-color: #c5cad3;
}

.condition-row >>> .ui.checkbox input:checked ~ label:before,
.condition-row >>> .ui.checkbox input:checked ~ .box:before {
  background: #fff;
  border-color: #B52222;
}

.condition-row >>> .ui.checkbox input:checked ~ label:after,
.condition-row >>> .ui.checkbox input:checked ~ .box:after {
  color: #B52222;
}
</style>